import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Paper,
    Button,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    Container,
    Chip,
    CardActions,
    Divider,
    Stack
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FunctionsIcon from '@mui/icons-material/Functions';
import SchoolIcon from '@mui/icons-material/School';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MathWorksheetGenerator from './worksheets/MathWorksheetGenerator';
import SubjectWorksheetGenerator from './worksheets/SubjectWorksheetGenerator';



const WorksheetGenerator = ({ setActiveTabGlobal }) => {
    const [selectedGenerator, setSelectedGenerator] = useState(null);
    const worksheetRef = useRef();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));


    const generators = [
        {
            id: 'math',
            title: 'Math Worksheet Generator',
            description: 'Create engaging math worksheets with various problem types and difficulty levels',
            icon: FunctionsIcon,
            component: MathWorksheetGenerator
        },
        {
            id: 'subject',
            title: 'Subject Worksheet Generator',
            description: 'Generate worksheets for any subject with customizable content and activities',
            icon: SchoolIcon,
            component: SubjectWorksheetGenerator
        }
    ];

    const handleBack = () => {
        setSelectedGenerator(null);
    };

    if (selectedGenerator) {
        const GeneratorComponent = selectedGenerator.component;
        <GeneratorComponent setActiveTabGlobal={setActiveTabGlobal} />
        return (
            <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
                <Fade in timeout={800}>
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexDirection: isSmallMobile ? 'column' : 'row',
                            gap: isSmallMobile ? 2 : 0
                        }}>
                            <Typography
                                variant={isSmallMobile ? "h5" : isMobile ? "h4" : "h3"}
                                fontWeight="bold"
                                sx={{
                                    color: theme.palette.primary.main,
                                    textAlign: isSmallMobile ? 'center' : 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                <selectedGenerator.icon sx={{ fontSize: 'inherit' }} />
                                {selectedGenerator.title}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                flexDirection: isSmallMobile ? 'column' : 'row'
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    sx={{
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.dark,
                                            backgroundColor: theme.palette.primary.light + '20'
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <ArrowBackIcon />
                                        <span>Back to Selection</span>
                                    </Stack>
                                </Button>
                            </Box>
                        </Box>

                        <Box ref={worksheetRef} sx={{
                            '@media print': {
                                '& *': {
                                    pageBreakInside: 'avoid',
                                    breakInside: 'avoid'
                                },
                                '& .MuiPaper-root': {
                                    borderRadius: 0,
                                    boxShadow: 'none',
                                    margin: '10px 0',
                                    padding: '15px'
                                }
                            }
                        }}>
                            <GeneratorComponent />
                        </Box>
                    </Box>
                </Fade>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 2, mt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
                            <AssignmentIcon sx={{ fontSize: '2rem', color: theme.palette.primary.main }} />
                            <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: theme.palette.primary.main }}>
                                Worksheet Generator
                            </Typography>
                        </Box>
                    </Box>

                    <Typography sx={{ fontSize: '1rem', fontStyle: 'italic', textAlign: 'center', mb: 4, color: theme.palette.text.secondary }}>
                        Choose the type of worksheet you'd like to generate for your students
                    </Typography>

                    <Grid container spacing={3}>
                        {generators.map((generator, index) => (
                            <Grid item xs={12} md={6} key={generator.id}>
                                <Zoom in timeout={600 + index * 200}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            border: `1px solid ${theme.palette.divider}`,
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: 8,
                                                borderColor: theme.palette.primary.main
                                            }
                                        }}
                                        onClick={() => setSelectedGenerator(generator)}
                                    >
                                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        fontSize: isSmallMobile ? '2.5rem' : '3rem',
                                                        mb: 1,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <generator.icon sx={{ fontSize: 'inherit' }} />
                                                </Box>
                                            </Box>

                                            <Typography
                                                variant={isSmallMobile ? "h6" : "h5"}
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    textAlign: 'center',
                                                    color: theme.palette.primary.main,
                                                    mb: 2
                                                }}
                                            >
                                                {generator.title}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    textAlign: 'center',
                                                    lineHeight: 1.6,
                                                    fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                                }}
                                            >
                                                {generator.description}
                                            </Typography>
                                        </CardContent>

                                        <Divider />

                                        <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{
                                                    py: 1.5,
                                                    borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                                    backgroundColor: theme.palette.primary.main,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.dark,
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 4
                                                    }
                                                }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PlayArrowIcon />
                                                    <span>Select Generator</span>
                                                </Stack>
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Fade>
        </Container>
    );
};

export default WorksheetGenerator;